// @ts-strict-ignore
import { SequenceOperation } from './sequence-operation';
import { ProjectModel } from '@project/model/project.model';
import { IOperation } from './i-operation';
import { UndoConnectionLabelOperation } from './undo-connection-label-operation';
import { UndoRemoveImageConnectionsOperation } from './undo-remove-image-connections-operation';
import { ArrayUtils } from '../../util/array-utils';
import { UndoDetachArticlesOperation } from './undo-detach-articles-operation';
import { UndoAttachArticlesOperation } from './undo-attach-articles-operation';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { PropertyOperation } from './property-operation';
import { Connection } from '@3d-scene/model3d/connection';
import { LoadConnectionTextureProcessor } from '@3d-scene/processor/load-connection-texture.processor';
import { LabelingUtils } from '@3d-scene/model3d/util/labeling-utils';
import { Material } from 'three';
import { UndoAttachUnusedLabelingArticlesOperation } from './undo-attach-unused-labeling-articles-operation';
import { UpdateLabelingCompressionProcessor } from '@3d-scene/processor/update-labeling-compression.processor';
import { ConnectionInfoService } from '@3d-scene/model3d/service/connection-info.service';

export class LabelSequenceOperation extends SequenceOperation {

  public constructor(
    operations: IOperation[],
    additionalOperations: IOperation[],
    private loadTextureProcessor: LoadConnectionTextureProcessor,
    private connectionInfoService: ConnectionInfoService,
    private containerService: ContainerService,
    private _labelingUtils: LabelingUtils,
    private updateLabelingCompressionProcessor: UpdateLabelingCompressionProcessor,
    private projectModel: ProjectModel = null,
    private forceUpdateTextures = false
  ) {
    super(operations, additionalOperations);
  }

  public getOperationDescription(): string {
    return '';
  }

  public async performRedo(): Promise<void> {
    await super.performRedo();
    await this.refreshTextureConnections();
  }

  public async performUndo(): Promise<void> {
    await super.performUndo();
    await this.refreshTextureConnections();
  }

  private async refreshTextureConnections(): Promise<void> {
    let hasPropertyOperations: boolean = false;
    let hasAttachDetachOperations: boolean = false;
    const connectionsToRefreshLabel: Connection[] = [];
    const articleUsageIdsToRefresh: string[] = [];
    for (const operation of this._operations) {
      if (operation instanceof UndoConnectionLabelOperation) {
        let articleUsageIds: string[] = [operation.metaUsageIdentity.articleUsageId];
        const connection: Connection = this.connectionInfoService.findConnectionByIdentity(operation.metaUsageIdentity);
        if (connection && connectionsToRefreshLabel.indexOf(connection) === -1) {
          let connections: Connection[] = [connection];

          if (operation.metaUsage.previousMergedUsageId || operation.metaUsage.nextMergedUsageId) {
            const firstMergedConnection: Connection = this._labelingUtils.findFirstMergedTextureCube(connection);
            connections = this._labelingUtils.findMergedConnections([firstMergedConnection || connection]);
          }

          connections.forEach((cn) => {
            cn.resetTexture();
            connectionsToRefreshLabel.push(cn);
          });

          articleUsageIds = connections.map((cn) => cn.articleContainer.articleGUID);
        }
        articleUsageIds.forEach((articleUsageId) => {
          if (articleUsageIdsToRefresh.indexOf(articleUsageId) === -1) {
            articleUsageIdsToRefresh.push(articleUsageId);
          }
        });
      } else if (operation instanceof UndoRemoveImageConnectionsOperation) {
        const articleGuidsWithImages: string[] = operation.getAllProcessedArticleGuids();
        ArrayUtils.mergeArrays(articleUsageIdsToRefresh, articleGuidsWithImages);
      } else if (operation instanceof PropertyOperation) {
        hasPropertyOperations = true;
      } else if (
        operation instanceof UndoDetachArticlesOperation ||
        operation instanceof UndoAttachArticlesOperation ||
        operation instanceof UndoAttachUnusedLabelingArticlesOperation
      ) {
        hasAttachDetachOperations = true;
      }
    }
    connectionsToRefreshLabel.forEach(cn => cn.articleContainer && cn.articleContainer.restoreConnection(cn.connectionName, (cn.mesh.material as Material).visible));
    await this.loadTextureProcessor.loadAndApplyImagesToConnectionsObservable(connectionsToRefreshLabel).toPromise();
    const containers: ArticleContainer[] = this.containerService.findArticleContainersInGlobalByGuids(articleUsageIdsToRefresh);
    for (const articleContainer of containers) {
      articleContainer.refreshTextureConnections();
    }
    if ((hasPropertyOperations && !hasAttachDetachOperations) || this.forceUpdateTextures) {
      if (this.projectModel) {
        this.projectModel.notifyCurrentProjectChanged();
        await this.updateLabelingCompressionProcessor.updateTextures();
      }
    }
  }
}
